import { useState, useEffect, useCallback } from 'react';
import api from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Plus, Search, Shield, AlertTriangle, HardHat, Flame, Cross, User, MapPin, Truck, Building2, Package, Check, X, Clock, FileText, History, UserPlus, RotateCcw, Trash2, Edit, Upload } from 'lucide-react';

const TYPE_CONFIG = {
  dpi: { label: 'DPI', icon: HardHat, color: 'text-blue-600 bg-blue-50 border-blue-200', desc: 'Dispositivi di Protezione Individuale' },
  dpc: { label: 'DPC', icon: Shield, color: 'text-green-600 bg-green-50 border-green-200', desc: 'Dispositivi di Protezione Collettiva' },
  estintore: { label: 'Estintori', icon: Flame, color: 'text-red-600 bg-red-50 border-red-200', desc: 'Estintori' },
  cassetta_medica: { label: 'Cassette Mediche', icon: Cross, color: 'text-purple-600 bg-purple-50 border-purple-200', desc: 'Cassette Mediche / Primo Soccorso' },
};

const STATUS_MAP = {
  disponibile: 'bg-green-50 text-green-700 border-green-200',
  assegnato: 'bg-blue-50 text-blue-700 border-blue-200',
  in_manutenzione: 'bg-amber-50 text-amber-700 border-amber-200',
  scaduto: 'bg-red-50 text-red-700 border-red-200',
  fuori_uso: 'bg-slate-100 text-slate-600 border-slate-200',
};

const EXPIRY_MAP = {
  valido: { className: 'bg-green-50 text-green-700 border-green-200', label: 'Valido' },
  in_scadenza: { className: 'bg-amber-50 text-amber-700 border-amber-200', label: 'In Scadenza' },
  scaduto: { className: 'bg-red-50 text-red-700 border-red-200', label: 'Scaduto' },
};

const DOC_TYPES = [
  { value: 'certificazione', label: 'Certificazione' },
  { value: 'scheda_tecnica', label: 'Scheda Tecnica' },
  { value: 'manuale', label: 'Manuale' },
  { value: 'verbale_verifica', label: 'Verbale Verifica' },
  { value: 'collaudo', label: 'Collaudo' },
  { value: 'altro', label: 'Altro' },
];

const ESTINTORE_FIELDS = [
  { key: 'controllo_semestrale_date', statusKey: 'controllo_semestrale_date_status', label: 'Controllo Semestrale' },
  { key: 'revisione_date', statusKey: 'revisione_date_status', label: 'Revisione' },
  { key: 'collaudo_date', statusKey: 'collaudo_date_status', label: 'Collaudo' },
  { key: 'fine_vita_date', statusKey: 'fine_vita_date_status', label: 'Fine Vita' },
];

const ESTINTORE_TYPES = [
  { value: 'polvere', label: 'Polvere' },
  { value: 'co2', label: 'CO2 (Anidride Carbonica)' },
  { value: 'schiuma', label: 'Schiuma' },
  { value: 'acqua', label: 'Acqua' },
  { value: 'idrico', label: 'Idrico' },
];

function mostUrgentEstintoreField(item) {
  if (!item.expiry_date) return null;
  return ESTINTORE_FIELDS.find(f => item[f.key] === item.expiry_date) || null;
}

const EMPTY_FORM = { name: '', code: '', serial_number: '', brand: '', model: '', status: 'disponibile', purchase_date: '', expiry_date: '', last_check_date: '', next_check_date: '', notes: '', assigned_location: '', assigned_site_id: '', assigned_warehouse: '', assigned_vehicle: '', dpi_category: 'I', dpi_type: '', controllo_semestrale_date: '', revisione_date: '', collaudo_date: '', fine_vita_date: '', estintore_type: 'polvere', capacity: '', manufacturing_year: '', manufacturer_code: '' };

function DeviceTab({ deviceType, employees, sites }) {
  const { isRole } = useAuth();
  const cfg = TYPE_CONFIG[deviceType];
  const isDPI = deviceType === 'dpi';
  const isEstintore = deviceType === 'estintore';
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState('');
  const [filterEmployee, setFilterEmployee] = useState('all');
  const [showCreate, setShowCreate] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [showDetail, setShowDetail] = useState(null);
  const [showAssign, setShowAssign] = useState(null);
  const [showHistory, setShowHistory] = useState(null);
  const [showDocAdd, setShowDocAdd] = useState(null);
  const [history, setHistory] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [form, setForm] = useState({ ...EMPTY_FORM });
  const [editForm, setEditForm] = useState({ ...EMPTY_FORM });
  const [assignForm, setAssignForm] = useState({ employee_id: '', notes: '' });
  const [docForm, setDocForm] = useState({ doc_type: 'certificazione', title: '', reference_number: '', issue_date: '', expiry_date: '', notes: '' });

  const load = useCallback(async () => {
    try {
      const params = { device_type: deviceType };
      if (search) params.search = search;
      if (isDPI && filterEmployee !== 'all') params.employee_id = filterEmployee;
      const res = await api.get('/safety-devices', { params });
      setItems(res.data);
    } catch {}
  }, [deviceType, search, filterEmployee, isDPI]);

  useEffect(() => { load(); }, [load]);

  const handleCreate = async () => {
    try {
      await api.post('/safety-devices', { ...form, device_type: deviceType });
      setShowCreate(false);
      setForm({ ...EMPTY_FORM });
      load();
    } catch (err) { alert(err.response?.data?.detail || 'Errore'); }
  };

  const openEdit = (item) => {
    setEditForm({ ...item });
    setShowEdit(item);
  };

  const handleSaveEdit = async () => {
    try {
      await api.put(`/safety-devices/${showEdit.id}`, editForm);
      setShowEdit(false);
      load();
    } catch (err) { alert(err.response?.data?.detail || 'Errore'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Eliminare questo dispositivo?')) return;
    try {
      await api.delete(`/safety-devices/${id}`);
      load();
    } catch (err) { alert(err.response?.data?.detail || 'Errore'); }
  };

  const openDetail = async (item) => {
    setShowDetail(item);
    try {
      const [hRes, dRes] = await Promise.all([
        api.get(`/safety-devices/${item.id}/history`),
        api.get(`/safety-devices/${item.id}/documents`),
      ]);
      setHistory(hRes.data);
      setDocuments(dRes.data);
      if (isDPI) {
        const aRes = await api.get(`/safety-devices/${item.id}/assignments`);
        setAssignments(aRes.data);
      }
    } catch {}
  };

  const handleAssign = async () => {
    try {
      await api.post(`/safety-devices/${showAssign.id}/assign`, assignForm);
      setShowAssign(null);
      setAssignForm({ employee_id: '', notes: '' });
      load();
      if (showDetail) openDetail(showDetail);
    } catch (err) { alert(err.response?.data?.detail || 'Errore'); }
  };

  const handleReturn = async (deviceId) => {
    try {
      await api.post(`/safety-devices/${deviceId}/return`, { notes: 'Restituito' });
      load();
      if (showDetail) openDetail(showDetail);
    } catch (err) { alert(err.response?.data?.detail || 'Errore'); }
  };

  const handleDocCreate = async () => {
    try {
      await api.post(`/safety-devices/${showDocAdd}/documents`, docForm);
      setShowDocAdd(null);
      setDocForm({ doc_type: 'certificazione', title: '', reference_number: '', issue_date: '', expiry_date: '', notes: '' });
      if (showDetail) openDetail(showDetail);
    } catch (err) { alert(err.response?.data?.detail || 'Errore'); }
  };

  const handleDocDelete = async (deviceId, docId) => {
    if (!window.confirm('Eliminare questo documento?')) return;
    try {
      await api.delete(`/safety-devices/${deviceId}/documents/${docId}`);
      if (showDetail) openDetail(showDetail);
    } catch {}
  };

  const canEdit = isRole('admin', 'qhse', 'aspp');

  const expiringCount = items.filter(i => i.expiry_status === 'in_scadenza').length;
  const expiredCount = items.filter(i => i.expiry_status === 'scaduto').length;

  return (
    <div className="space-y-4">
      {/* KPI */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Card><CardContent className="pt-4 pb-3"><p className="text-[10px] font-semibold uppercase text-slate-400">Totale</p><p className="text-2xl font-black">{items.length}</p></CardContent></Card>
        <Card><CardContent className="pt-4 pb-3"><p className="text-[10px] font-semibold uppercase text-slate-400">Disponibili</p><p className="text-2xl font-black text-green-600">{items.filter(i => i.status === 'disponibile').length}</p></CardContent></Card>
        <Card className={expiringCount > 0 ? 'border-amber-300' : ''}><CardContent className="pt-4 pb-3"><p className="text-[10px] font-semibold uppercase text-slate-400">In Scadenza</p><p className="text-2xl font-black text-amber-600">{expiringCount}</p></CardContent></Card>
        <Card className={expiredCount > 0 ? 'border-red-300' : ''}><CardContent className="pt-4 pb-3"><p className="text-[10px] font-semibold uppercase text-slate-400">Scaduti</p><p className="text-2xl font-black text-red-600">{expiredCount}</p></CardContent></Card>
      </div>

      {/* Filters & Actions */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 sm:w-48">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input placeholder="Cerca..." value={search} onChange={e => setSearch(e.target.value)} className="pl-9" data-testid={`search-${deviceType}`} />
        </div>
        {isDPI && (
          <Select value={filterEmployee} onValueChange={setFilterEmployee}>
            <SelectTrigger className="w-48" data-testid="filter-employee"><SelectValue placeholder="Dipendente" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tutti i dipendenti</SelectItem>
              {employees.map(e => <SelectItem key={e.id} value={e.id}>{e.first_name} {e.last_name}</SelectItem>)}
            </SelectContent>
          </Select>
        )}
        {canEdit && (
          <Button onClick={() => setShowCreate(true)} data-testid={`add-${deviceType}-btn`}><Plus size={16} className="mr-1" /> Nuovo {cfg.label}</Button>
        )}
      </div>

      {/* Table */}
      <Card>
        <CardContent className="p-0 overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Nome</TableHead>
                <TableHead className="hidden sm:table-cell">{isEstintore ? 'Matricola' : 'Codice'}</TableHead>
                {isDPI && <TableHead className="hidden md:table-cell">Cat.</TableHead>}
                {isEstintore && <TableHead className="hidden md:table-cell">Tipo</TableHead>}
                <TableHead>Stato</TableHead>
                <TableHead>Scadenza</TableHead>
                {isDPI ? <TableHead>Assegnato a</TableHead> : <TableHead className="hidden lg:table-cell">Posizione</TableHead>}
                {canEdit && <TableHead className="w-28">Azioni</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.length === 0 ? (
                <TableRow><TableCell colSpan={isDPI ? 7 : 7} className="text-center py-8 text-slate-400">Nessun dispositivo</TableCell></TableRow>
              ) : items.map(item => (
                <TableRow key={item.id} className="hover:bg-slate-50 cursor-pointer" onClick={() => openDetail(item)} data-testid={`device-row-${item.id}`}>
                  <TableCell>
                    <p className="text-sm font-medium">{item.name}</p>
                    <p className="text-xs text-slate-500">{item.brand} {item.model}</p>
                  </TableCell>
                  <TableCell className="hidden sm:table-cell text-sm text-slate-600">{isEstintore ? (item.serial_number || item.code || '-') : (item.code || item.serial_number || '-')}{isEstintore && (item.manufacturing_year || item.manufacturer_code) && <p className="text-xs text-slate-400">{[item.manufacturing_year && `Anno ${item.manufacturing_year}`, item.manufacturer_code && `Cod. costr. ${item.manufacturer_code}`].filter(Boolean).join(' · ')}</p>}</TableCell>
                  {isDPI && <TableCell className="hidden md:table-cell"><Badge variant="outline" className="text-xs">Cat. {item.dpi_category}</Badge></TableCell>}
                  {isEstintore && <TableCell className="hidden md:table-cell text-sm text-slate-600">{ESTINTORE_TYPES.find(t => t.value === item.estintore_type)?.label || '-'}{item.capacity ? ` (${item.capacity})` : ''}</TableCell>}
                  <TableCell><Badge variant="outline" className={STATUS_MAP[item.status] || STATUS_MAP.disponibile}>{item.status?.replace('_', ' ')}</Badge></TableCell>
                  <TableCell>
                    {item.expiry_date ? (
                      <div className="space-y-0.5">
                        <Badge variant="outline" className={EXPIRY_MAP[item.expiry_status]?.className}>
                          {new Date(item.expiry_date).toLocaleDateString('it-IT')}
                        </Badge>
                        {isEstintore && mostUrgentEstintoreField(item) && (
                          <p className="text-[10px] text-slate-400">{mostUrgentEstintoreField(item).label}</p>
                        )}
                      </div>
                    ) : '-'}
                  </TableCell>
                  {isDPI ? (
                    <TableCell className="text-sm">{item.assigned_employee_name || <span className="text-slate-400">Non assegnato</span>}</TableCell>
                  ) : (
                    <TableCell className="hidden lg:table-cell text-sm text-slate-600">{item.assigned_location || item.assigned_site_name || '-'}</TableCell>
                  )}
                  {canEdit && (
                    <TableCell>
                      <div className="flex gap-1" onClick={e => e.stopPropagation()}>
                        {isDPI && !item.assigned_employee_id && (
                          <Button size="icon" variant="ghost" className="h-7 w-7" title="Assegna" onClick={() => setShowAssign(item)} data-testid={`assign-btn-${item.id}`}><UserPlus size={14} /></Button>
                        )}
                        {isDPI && item.assigned_employee_id && (
                          <Button size="icon" variant="ghost" className="h-7 w-7" title="Restituisci" onClick={() => handleReturn(item.id)} data-testid={`return-btn-${item.id}`}><RotateCcw size={14} /></Button>
                        )}
                        <Button size="icon" variant="ghost" className="h-7 w-7" title="Modifica" onClick={() => openEdit(item)}><Edit size={14} /></Button>
                        {isRole('admin', 'aspp') && <Button size="icon" variant="ghost" className="h-7 w-7 text-red-600" title="Elimina" onClick={() => handleDelete(item.id)}><Trash2 size={14} /></Button>}
                      </div>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* CREATE DIALOG */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Nuovo {cfg.label}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Input placeholder="Nome / Descrizione" value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} data-testid="create-name" />
            {isDPI && (
              <>
                <Input placeholder="Tipologia DPI" value={form.dpi_type} onChange={e => setForm(p => ({ ...p, dpi_type: e.target.value }))} data-testid="create-dpi-type" />
                <Select value={form.dpi_category} onValueChange={v => setForm(p => ({ ...p, dpi_category: v }))}>
                  <SelectTrigger data-testid="create-dpi-category"><SelectValue placeholder="Categoria DPI" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="I">Categoria I</SelectItem>
                    <SelectItem value="II">Categoria II</SelectItem>
                    <SelectItem value="III">Categoria III</SelectItem>
                  </SelectContent>
                </Select>
              </>
            )}
            {isEstintore && (
              <div className="grid grid-cols-2 gap-3">
                <Select value={form.estintore_type} onValueChange={v => setForm(p => ({ ...p, estintore_type: v }))}>
                  <SelectTrigger data-testid="create-estintore-type"><SelectValue placeholder="Tipo estintore" /></SelectTrigger>
                  <SelectContent>{ESTINTORE_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
                </Select>
                <Input placeholder="Carica (es. 6 kg)" value={form.capacity} onChange={e => setForm(p => ({ ...p, capacity: e.target.value }))} data-testid="create-capacity" />
              </div>
            )}
            {isEstintore && (
              <div className="grid grid-cols-2 gap-3">
                <Input placeholder="Anno di fabbricazione (es. 2019)" inputMode="numeric" maxLength={4} value={form.manufacturing_year} onChange={e => setForm(p => ({ ...p, manufacturing_year: e.target.value.replace(/\D/g, '') }))} data-testid="create-manufacturing-year" />
                <Input placeholder="Codice costruttore" value={form.manufacturer_code} onChange={e => setForm(p => ({ ...p, manufacturer_code: e.target.value }))} data-testid="create-manufacturer-code" />
              </div>
            )}
            <div className="grid grid-cols-2 gap-3">
              <Input placeholder={isEstintore ? 'Codice interno' : 'Codice / Matricola'} value={form.code} onChange={e => setForm(p => ({ ...p, code: e.target.value }))} data-testid="create-code" />
              <Input placeholder="Matricola / N. Serie" value={form.serial_number} onChange={e => setForm(p => ({ ...p, serial_number: e.target.value }))} data-testid="create-serial" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Input placeholder="Marca" value={form.brand} onChange={e => setForm(p => ({ ...p, brand: e.target.value }))} />
              <Input placeholder="Modello" value={form.model} onChange={e => setForm(p => ({ ...p, model: e.target.value }))} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><label className="text-xs text-slate-500">Data acquisto</label><Input type="date" value={form.purchase_date} onChange={e => setForm(p => ({ ...p, purchase_date: e.target.value }))} /></div>
              {!isEstintore && (
                <div><label className="text-xs text-slate-500">Data scadenza</label><Input type="date" value={form.expiry_date} onChange={e => setForm(p => ({ ...p, expiry_date: e.target.value }))} data-testid="create-expiry" /></div>
              )}
            </div>
            {isEstintore && (
              <div className="grid grid-cols-2 gap-3">
                <div><label className="text-xs text-slate-500">Controllo Semestrale</label><Input type="date" value={form.controllo_semestrale_date} onChange={e => setForm(p => ({ ...p, controllo_semestrale_date: e.target.value }))} data-testid="create-controllo-semestrale" /></div>
                <div><label className="text-xs text-slate-500">Revisione</label><Input type="date" value={form.revisione_date} onChange={e => setForm(p => ({ ...p, revisione_date: e.target.value }))} data-testid="create-revisione" /></div>
                <div><label className="text-xs text-slate-500">Collaudo</label><Input type="date" value={form.collaudo_date} onChange={e => setForm(p => ({ ...p, collaudo_date: e.target.value }))} data-testid="create-collaudo" /></div>
                <div><label className="text-xs text-slate-500">Fine Vita</label><Input type="date" value={form.fine_vita_date} onChange={e => setForm(p => ({ ...p, fine_vita_date: e.target.value }))} data-testid="create-fine-vita" /></div>
              </div>
            )}
            {!isDPI && (
              <>
                <Input placeholder="Luogo / Posizione" value={form.assigned_location} onChange={e => setForm(p => ({ ...p, assigned_location: e.target.value }))} data-testid="create-location" />
                <Select value={form.assigned_site_id || 'none'} onValueChange={v => setForm(p => ({ ...p, assigned_site_id: v === 'none' ? '' : v }))}>
                  <SelectTrigger data-testid="create-site"><SelectValue placeholder="Cantiere" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Nessun cantiere</SelectItem>
                    {sites.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                  </SelectContent>
                </Select>
                <div className="grid grid-cols-2 gap-3">
                  <Input placeholder="Magazzino" value={form.assigned_warehouse} onChange={e => setForm(p => ({ ...p, assigned_warehouse: e.target.value }))} />
                  <Input placeholder="Mezzo" value={form.assigned_vehicle} onChange={e => setForm(p => ({ ...p, assigned_vehicle: e.target.value }))} />
                </div>
              </>
            )}
            <Input placeholder="Note" value={form.notes} onChange={e => setForm(p => ({ ...p, notes: e.target.value }))} />
            <Button className="w-full" onClick={handleCreate} disabled={!form.name} data-testid="create-submit">Crea {cfg.label}</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* EDIT DIALOG */}
      <Dialog open={!!showEdit} onOpenChange={() => setShowEdit(false)}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Modifica {cfg.label}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Input placeholder="Nome" value={editForm.name} onChange={e => setEditForm(p => ({ ...p, name: e.target.value }))} />
            {isDPI && (
              <>
                <Input placeholder="Tipologia DPI" value={editForm.dpi_type || ''} onChange={e => setEditForm(p => ({ ...p, dpi_type: e.target.value }))} />
                <Select value={editForm.dpi_category || 'I'} onValueChange={v => setEditForm(p => ({ ...p, dpi_category: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="I">Categoria I</SelectItem>
                    <SelectItem value="II">Categoria II</SelectItem>
                    <SelectItem value="III">Categoria III</SelectItem>
                  </SelectContent>
                </Select>
              </>
            )}
            {isEstintore && (
              <div className="grid grid-cols-2 gap-3">
                <Select value={editForm.estintore_type || 'polvere'} onValueChange={v => setEditForm(p => ({ ...p, estintore_type: v }))}>
                  <SelectTrigger><SelectValue placeholder="Tipo estintore" /></SelectTrigger>
                  <SelectContent>{ESTINTORE_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
                </Select>
                <Input placeholder="Carica (es. 6 kg)" value={editForm.capacity || ''} onChange={e => setEditForm(p => ({ ...p, capacity: e.target.value }))} />
              </div>
            )}
            {isEstintore && (
              <div className="grid grid-cols-2 gap-3">
                <Input placeholder="Anno di fabbricazione (es. 2019)" inputMode="numeric" maxLength={4} value={editForm.manufacturing_year || ''} onChange={e => setEditForm(p => ({ ...p, manufacturing_year: e.target.value.replace(/\D/g, '') }))} data-testid="edit-manufacturing-year" />
                <Input placeholder="Codice costruttore" value={editForm.manufacturer_code || ''} onChange={e => setEditForm(p => ({ ...p, manufacturer_code: e.target.value }))} data-testid="edit-manufacturer-code" />
              </div>
            )}
            <div className="grid grid-cols-2 gap-3">
              <Input placeholder={isEstintore ? 'Codice interno' : 'Codice'} value={editForm.code || ''} onChange={e => setEditForm(p => ({ ...p, code: e.target.value }))} />
              <Input placeholder="Matricola / N. Serie" value={editForm.serial_number || ''} onChange={e => setEditForm(p => ({ ...p, serial_number: e.target.value }))} />
            </div>
            <Select value={editForm.status} onValueChange={v => setEditForm(p => ({ ...p, status: v }))}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="disponibile">Disponibile</SelectItem>
                <SelectItem value="assegnato">Assegnato</SelectItem>
                <SelectItem value="in_manutenzione">In Manutenzione</SelectItem>
                <SelectItem value="scaduto">Scaduto</SelectItem>
                <SelectItem value="fuori_uso">Fuori Uso</SelectItem>
              </SelectContent>
            </Select>
            <div className="grid grid-cols-2 gap-3">
              <div><label className="text-xs text-slate-500">Data acquisto</label><Input type="date" value={editForm.purchase_date || ''} onChange={e => setEditForm(p => ({ ...p, purchase_date: e.target.value }))} /></div>
              {!isEstintore && (
                <div><label className="text-xs text-slate-500">Data scadenza</label><Input type="date" value={editForm.expiry_date || ''} onChange={e => setEditForm(p => ({ ...p, expiry_date: e.target.value }))} /></div>
              )}
            </div>
            {isEstintore && (
              <div className="grid grid-cols-2 gap-3">
                <div><label className="text-xs text-slate-500">Controllo Semestrale</label><Input type="date" value={editForm.controllo_semestrale_date || ''} onChange={e => setEditForm(p => ({ ...p, controllo_semestrale_date: e.target.value }))} data-testid="edit-controllo-semestrale" /></div>
                <div><label className="text-xs text-slate-500">Revisione</label><Input type="date" value={editForm.revisione_date || ''} onChange={e => setEditForm(p => ({ ...p, revisione_date: e.target.value }))} data-testid="edit-revisione" /></div>
                <div><label className="text-xs text-slate-500">Collaudo</label><Input type="date" value={editForm.collaudo_date || ''} onChange={e => setEditForm(p => ({ ...p, collaudo_date: e.target.value }))} data-testid="edit-collaudo" /></div>
                <div><label className="text-xs text-slate-500">Fine Vita</label><Input type="date" value={editForm.fine_vita_date || ''} onChange={e => setEditForm(p => ({ ...p, fine_vita_date: e.target.value }))} data-testid="edit-fine-vita" /></div>
              </div>
            )}
            {!isDPI && (
              <>
                <Input placeholder="Luogo / Posizione" value={editForm.assigned_location || ''} onChange={e => setEditForm(p => ({ ...p, assigned_location: e.target.value }))} />
                <Select value={editForm.assigned_site_id || 'none'} onValueChange={v => setEditForm(p => ({ ...p, assigned_site_id: v === 'none' ? '' : v }))}>
                  <SelectTrigger><SelectValue placeholder="Cantiere" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Nessun cantiere</SelectItem>
                    {sites.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                  </SelectContent>
                </Select>
                <div className="grid grid-cols-2 gap-3">
                  <Input placeholder="Magazzino" value={editForm.assigned_warehouse || ''} onChange={e => setEditForm(p => ({ ...p, assigned_warehouse: e.target.value }))} />
                  <Input placeholder="Mezzo" value={editForm.assigned_vehicle || ''} onChange={e => setEditForm(p => ({ ...p, assigned_vehicle: e.target.value }))} />
                </div>
              </>
            )}
            <Input placeholder="Note" value={editForm.notes || ''} onChange={e => setEditForm(p => ({ ...p, notes: e.target.value }))} />
            <Button className="w-full" onClick={handleSaveEdit}>Salva Modifiche</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* ASSIGN DPI DIALOG */}
      {isDPI && (
        <Dialog open={!!showAssign} onOpenChange={() => setShowAssign(null)}>
          <DialogContent>
            <DialogHeader><DialogTitle>Assegna DPI a Dipendente</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <p className="text-sm text-slate-500">DPI: <strong>{showAssign?.name}</strong></p>
              <Select value={assignForm.employee_id} onValueChange={v => setAssignForm(p => ({ ...p, employee_id: v }))}>
                <SelectTrigger data-testid="assign-employee-select"><SelectValue placeholder="Seleziona dipendente" /></SelectTrigger>
                <SelectContent>{employees.map(e => <SelectItem key={e.id} value={e.id}>{e.first_name} {e.last_name}</SelectItem>)}</SelectContent>
              </Select>
              <Input placeholder="Note" value={assignForm.notes} onChange={e => setAssignForm(p => ({ ...p, notes: e.target.value }))} />
              <Button className="w-full" onClick={handleAssign} disabled={!assignForm.employee_id} data-testid="assign-submit">Assegna</Button>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* DETAIL DIALOG */}
      <Dialog open={!!showDetail} onOpenChange={() => setShowDetail(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{showDetail?.name}</DialogTitle></DialogHeader>
          {showDetail && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-sm">
                {isEstintore ? (
                  <>
                    <div><span className="text-slate-400 text-xs">Matricola</span><p className="font-medium">{showDetail.serial_number || '-'}</p></div>
                    <div><span className="text-slate-400 text-xs">Codice interno</span><p className="font-medium">{showDetail.code || '-'}</p></div>
                    <div><span className="text-slate-400 text-xs">Anno di fabbricazione</span><p className="font-medium" data-testid="detail-manufacturing-year">{showDetail.manufacturing_year || '-'}</p></div>
                    <div><span className="text-slate-400 text-xs">Codice costruttore</span><p className="font-medium" data-testid="detail-manufacturer-code">{showDetail.manufacturer_code || '-'}</p></div>
                    <div><span className="text-slate-400 text-xs">Tipo</span><p className="font-medium">{ESTINTORE_TYPES.find(t => t.value === showDetail.estintore_type)?.label || '-'}{showDetail.capacity ? ` - ${showDetail.capacity}` : ''}</p></div>
                  </>
                ) : (
                  <div><span className="text-slate-400 text-xs">Codice</span><p className="font-medium">{showDetail.code || showDetail.serial_number || '-'}</p></div>
                )}
                <div><span className="text-slate-400 text-xs">Marca / Modello</span><p className="font-medium">{showDetail.brand} {showDetail.model}</p></div>
                <div><span className="text-slate-400 text-xs">Stato</span><div><Badge variant="outline" className={STATUS_MAP[showDetail.status]}>{showDetail.status?.replace('_', ' ')}</Badge></div></div>
                {isDPI && <div><span className="text-slate-400 text-xs">Categoria DPI</span><p className="font-medium">Cat. {showDetail.dpi_category}</p></div>}
                {isDPI && <div><span className="text-slate-400 text-xs">Assegnato a</span><p className="font-medium">{showDetail.assigned_employee_name || 'Non assegnato'}</p></div>}
                {!isDPI && <div><span className="text-slate-400 text-xs">Posizione</span><p className="font-medium">{showDetail.assigned_location || showDetail.assigned_site_name || '-'}</p></div>}
                {!isEstintore && <div><span className="text-slate-400 text-xs">Scadenza</span><p className="font-medium">{showDetail.expiry_date ? new Date(showDetail.expiry_date).toLocaleDateString('it-IT') : '-'}</p></div>}
                <div><span className="text-slate-400 text-xs">Data Acquisto</span><p className="font-medium">{showDetail.purchase_date ? new Date(showDetail.purchase_date).toLocaleDateString('it-IT') : '-'}</p></div>
                {showDetail.notes && <div className="col-span-2 sm:col-span-3"><span className="text-slate-400 text-xs">Note</span><p className="font-medium">{showDetail.notes}</p></div>}
              </div>

              {isEstintore && (
                <div>
                  <h3 className="text-sm font-semibold mb-2 flex items-center gap-1"><Flame size={14} /> Scadenze Estintore</h3>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                    {ESTINTORE_FIELDS.map(f => (
                      <div key={f.key} className="p-2 rounded border bg-slate-50">
                        <p className="text-[10px] uppercase font-semibold text-slate-400">{f.label}</p>
                        {showDetail[f.key] ? (
                          <>
                            <p className="text-sm font-medium">{new Date(showDetail[f.key]).toLocaleDateString('it-IT')}</p>
                            <Badge variant="outline" className={EXPIRY_MAP[showDetail[f.statusKey]]?.className + ' text-xs mt-1'}>{EXPIRY_MAP[showDetail[f.statusKey]]?.label}</Badge>
                          </>
                        ) : <p className="text-sm text-slate-400">Non impostata</p>}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Actions */}
              {canEdit && (
                <div className="flex gap-2 flex-wrap">
                  {isDPI && !showDetail.assigned_employee_id && <Button size="sm" onClick={() => setShowAssign(showDetail)}><UserPlus size={14} className="mr-1" /> Assegna</Button>}
                  {isDPI && showDetail.assigned_employee_id && <Button size="sm" variant="outline" onClick={() => handleReturn(showDetail.id)}><RotateCcw size={14} className="mr-1" /> Restituisci</Button>}
                  <Button size="sm" variant="outline" onClick={() => setShowDocAdd(showDetail.id)}><FileText size={14} className="mr-1" /> Aggiungi Documento</Button>
                </div>
              )}

              {/* Documents */}
              <div>
                <h3 className="text-sm font-semibold mb-2 flex items-center gap-1"><FileText size={14} /> Documenti ({documents.length})</h3>
                {documents.length === 0 ? <p className="text-xs text-slate-400">Nessun documento</p> : (
                  <div className="space-y-2">
                    {documents.map(d => (
                      <div key={d.id} className="flex items-center justify-between p-2 rounded border bg-slate-50 text-sm">
                        <div>
                          <p className="font-medium">{d.title}</p>
                          <p className="text-xs text-slate-500">{DOC_TYPES.find(dt => dt.value === d.doc_type)?.label} {d.expiry_date ? `- Scade: ${new Date(d.expiry_date).toLocaleDateString('it-IT')}` : ''}</p>
                        </div>
                        <div className="flex gap-1 items-center">
                          {d.expiry_date && <Badge variant="outline" className={EXPIRY_MAP[d.expiry_status]?.className + ' text-xs'}>{EXPIRY_MAP[d.expiry_status]?.label}</Badge>}
                          {canEdit && <Button size="icon" variant="ghost" className="h-6 w-6 text-red-500" onClick={() => handleDocDelete(showDetail.id, d.id)}><Trash2 size={12} /></Button>}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Assignment History (DPI only) */}
              {isDPI && (
                <div>
                  <h3 className="text-sm font-semibold mb-2 flex items-center gap-1"><User size={14} /> Storico Assegnazioni ({assignments.length})</h3>
                  {assignments.length === 0 ? <p className="text-xs text-slate-400">Nessuna assegnazione</p> : (
                    <div className="space-y-2">
                      {assignments.map(a => (
                        <div key={a.id} className="flex items-center justify-between p-2 rounded border bg-slate-50 text-sm">
                          <div>
                            <p className="font-medium">{a.employee_name}</p>
                            <p className="text-xs text-slate-500">Dal {new Date(a.assigned_at).toLocaleDateString('it-IT')}{a.returned_at ? ` al ${new Date(a.returned_at).toLocaleDateString('it-IT')}` : ' (attivo)'}</p>
                          </div>
                          {!a.returned_at && <Badge className="bg-green-100 text-green-800 text-xs">Attivo</Badge>}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* History */}
              <div>
                <h3 className="text-sm font-semibold mb-2 flex items-center gap-1"><History size={14} /> Storico ({history.length})</h3>
                {history.length === 0 ? <p className="text-xs text-slate-400">Nessuna attivita</p> : (
                  <div className="space-y-1">
                    {history.slice(0, 10).map(h => (
                      <div key={h.id} className="flex items-center gap-2 p-2 rounded border bg-slate-50 text-xs">
                        <Clock size={12} className="text-slate-400" />
                        <span className="text-slate-500">{new Date(h.timestamp).toLocaleDateString('it-IT')}</span>
                        <span className="font-medium">{h.action}</span>
                        <span className="text-slate-500 flex-1 truncate">{h.details}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* ADD DOCUMENT DIALOG */}
      <Dialog open={!!showDocAdd} onOpenChange={() => setShowDocAdd(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Aggiungi Documento</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Select value={docForm.doc_type} onValueChange={v => setDocForm(p => ({ ...p, doc_type: v }))}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>{DOC_TYPES.map(d => <SelectItem key={d.value} value={d.value}>{d.label}</SelectItem>)}</SelectContent>
            </Select>
            <Input placeholder="Titolo" value={docForm.title} onChange={e => setDocForm(p => ({ ...p, title: e.target.value }))} data-testid="doc-title" />
            <Input placeholder="Numero riferimento" value={docForm.reference_number} onChange={e => setDocForm(p => ({ ...p, reference_number: e.target.value }))} />
            <div className="grid grid-cols-2 gap-3">
              <div><label className="text-xs text-slate-500">Data emissione</label><Input type="date" value={docForm.issue_date} onChange={e => setDocForm(p => ({ ...p, issue_date: e.target.value }))} /></div>
              <div><label className="text-xs text-slate-500">Data scadenza</label><Input type="date" value={docForm.expiry_date} onChange={e => setDocForm(p => ({ ...p, expiry_date: e.target.value }))} /></div>
            </div>
            <Input placeholder="Note" value={docForm.notes} onChange={e => setDocForm(p => ({ ...p, notes: e.target.value }))} />
            <Button className="w-full" onClick={handleDocCreate} disabled={!docForm.title}>Salva Documento</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ─── MAIN PAGE ───
export default function SafetyDevicesPage() {
  const [employees, setEmployees] = useState([]);
  const [sites, setSites] = useState([]);

  useEffect(() => {
    Promise.all([
      api.get('/employees').catch(() => ({ data: [] })),
      api.get('/sites').catch(() => ({ data: [] })),
    ]).then(([empRes, siteRes]) => {
      setEmployees(Array.isArray(empRes.data) ? empRes.data.filter(e => e.is_active) : []);
      setSites(Array.isArray(siteRes.data) ? siteRes.data : []);
    });
  }, []);

  return (
    <div className="space-y-6 animate-fade-in" data-testid="safety-devices-page">
      <div>
        <h1 className="text-2xl font-bold text-slate-900 tracking-tight" data-testid="safety-devices-title">Dispositivi di Sicurezza</h1>
        <p className="text-sm text-slate-500">DPI, DPC, Estintori e Cassette Mediche</p>
      </div>

      <Tabs defaultValue="dpi" data-testid="safety-devices-tabs">
        <TabsList className="flex-wrap h-auto gap-1">
          {Object.entries(TYPE_CONFIG).map(([key, cfg]) => {
            const Icon = cfg.icon;
            return (
              <TabsTrigger key={key} value={key} data-testid={`tab-${key}`} className="gap-1.5">
                <Icon size={14} /> {cfg.label}
              </TabsTrigger>
            );
          })}
        </TabsList>

        {Object.keys(TYPE_CONFIG).map(key => (
          <TabsContent key={key} value={key}>
            <DeviceTab deviceType={key} employees={employees} sites={sites} />
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
}
